import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Package, PackageGroupSize } from '../../types/Package';
import { loadServicesFromServer, saveServicesToServer } from '../../utils/ServiceAPI';
import UploadWidget from '../Cloudinary/UploadWidget';
import CloudinaryImageBrowser from '../Cloudinary/CloudinaryImageBrowser';
import { CloudinaryImage } from '../../types/Gallery';
import { convertCurrencyWithMarkup, loadCurrencySettings, formatCurrency, getCurrencySymbol } from '../../utils/CurrencyUtils';
import { getCategoryOptions, getCategoryEmoji, loadCategoriesFromServer, Category } from '../../utils/categories';
// Import package templates - updates to packageTemplates.ts will be reflected here
import { PACKAGE_TEMPLATES, parseGroupSize } from '../../utils/packageTemplates';
import PackageCreateWizard from './PackageCreateWizard';
import './ServiceManagement.css';

// Currency conversion table component
const CurrencyConversionTable: React.FC<{ package: EditState }> = ({ package: packageItem }) => {
  const [currencyData, setCurrencyData] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCurrencyConversions = async () => {
      if (!packageItem.price || !packageItem.currency) return;
      
      setLoading(true);
      const currencySettings = loadCurrencySettings();
      const baseCurrency = packageItem.currency;
      const baseAmount = packageItem.price;
      const specialAmount = packageItem.specialPrice || packageItem.price;
      
      // Ensure we have valid numbers and currency
      if (typeof baseAmount !== 'number' || typeof specialAmount !== 'number' || !baseCurrency) {
        setLoading(false);
        return;
      }
      
      const conversions: { [key: string]: any } = {};
      
      // Convert to all supported currencies
      const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
      if (baseCurrency !== 'R') currencies.push('R');
      
      for (const targetCurrency of currencies) {
        if (targetCurrency === baseCurrency) continue;
        
        try {
          const baseConversion = await convertCurrencyWithMarkup(baseAmount, baseCurrency, targetCurrency, currencySettings);
          const specialConversion = await convertCurrencyWithMarkup(specialAmount, baseCurrency, targetCurrency, currencySettings);
          
          conversions[targetCurrency] = {
            base: baseConversion,
            special: specialConversion,
            enabled: currencySettings[targetCurrency]?.enabled || false
          };
        } catch (error) {
          console.error(`Failed to convert to ${targetCurrency}:`, error);
        }
      }
      
      setCurrencyData(conversions);
      setLoading(false);
    };

    loadCurrencyConversions();
  }, [packageItem.price, packageItem.specialPrice, packageItem.currency]);

  // Listen for currency settings changes to refresh conversions
  useEffect(() => {
    const handleCurrencySettingsUpdate = () => {
      console.log('🔄 Currency settings updated, refreshing conversions...');
      // Re-trigger the currency conversion loading
      if (packageItem.price && packageItem.currency) {
        const loadCurrencyConversions = async () => {
          setLoading(true);
          const currencySettings = loadCurrencySettings();
          const baseCurrency = packageItem.currency;
          const baseAmount = packageItem.price;
          const specialAmount = packageItem.specialPrice || packageItem.price;
          
          // Ensure we have valid numbers and currency
          if (typeof baseAmount !== 'number' || typeof specialAmount !== 'number' || !baseCurrency) {
            setLoading(false);
            return;
          }
          
          const conversions: { [key: string]: any } = {};
          
          const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
          if (baseCurrency !== 'R') currencies.push('R');
          
          for (const targetCurrency of currencies) {
            if (targetCurrency === baseCurrency) continue;
            
            try {
              const baseConversion = await convertCurrencyWithMarkup(baseAmount, baseCurrency, targetCurrency, currencySettings);
              const specialConversion = await convertCurrencyWithMarkup(specialAmount, baseCurrency, targetCurrency, currencySettings);
              
              conversions[targetCurrency] = {
                base: baseConversion,
                special: specialConversion,
                enabled: currencySettings[targetCurrency]?.enabled || false
              };
            } catch (error) {
              console.error(`Failed to convert to ${targetCurrency}:`, error);
            }
          }
          
          setCurrencyData(conversions);
          setLoading(false);
        };
        
        loadCurrencyConversions();
      }
    };

    // Listen for currency settings updates
    window.addEventListener('currency_settings_updated', handleCurrencySettingsUpdate);
    
    return () => {
      window.removeEventListener('currency_settings_updated', handleCurrencySettingsUpdate);
    };
  }, [packageItem.price, packageItem.specialPrice, packageItem.currency]);

  if (!packageItem.price) {
    return (
      <div className="currency-table-placeholder">
        <i className="fas fa-info-circle"></i>
        <p>Set a base price to see currency conversions</p>
      </div>
    );
  }

  return (
    <div className="currency-conversion-table">
      <div className="table-header">
        <p>Automatic conversions with markup and rounding applied</p>
      </div>
      
      {loading ? (
        <div className="currency-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading exchange rates...</span>
        </div>
      ) : (
        <div className="currency-table">
          <div className="table-row header-row">
            <div className="currency-col">Currency</div>
            <div className="price-col">Base Price</div>
            <div className="price-col">Special Price</div>
            <div className="markup-col">Markup</div>
            <div className="status-col">Status</div>
          </div>
          
          {Object.entries(currencyData).map(([currency, data]) => (
            <div key={currency} className={`table-row ${data.enabled ? 'enabled' : 'disabled'}`}>
              <div className="currency-col">
                <span className="currency-code">{currency}</span>
                <span className="currency-symbol">{getCurrencySymbol(currency)}</span>
              </div>
              <div className="price-col">
                <span className="converted-price">
                  {getCurrencySymbol(currency)} {data.base.markupAmount.toFixed(0)}
                </span>
                {data.base.rounded && <span className="rounded-indicator" title="Rounded up">↗</span>}
              </div>
              <div className="price-col">
                <span className="converted-price">
                  {getCurrencySymbol(currency)} {data.special.markupAmount.toFixed(0)}
                </span>
                {data.special.rounded && <span className="rounded-indicator" title="Rounded up">↗</span>}
              </div>
              <div className="markup-col">
                <span className="markup-percentage">+{data.base.markupPercentage}%</span>
              </div>
              <div className="status-col">
                <span className={`status-badge ${data.enabled ? 'enabled' : 'disabled'}`}>
                  {data.enabled ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="currency-table-footer">
        <p>
          <i className="fas fa-info-circle"></i>
          Exchange rates update every 5 minutes. Markup and rounding configured in Currency Management.
        </p>
      </div>
    </div>
  );
};

// Preview component for the package modal
const PackagePreview: React.FC<{ package: EditState }> = ({ package: packageItem }) => {
  const [customizationData, setCustomizationData] = useState<any>(null);

  useEffect(() => {
    // Use package-specific customization if available, otherwise use global defaults
    if (packageItem.customization) {
      setCustomizationData(packageItem.customization);
    } else {
      const savedData = localStorage.getItem('package_customization');
      if (savedData) {
        try {
          setCustomizationData(JSON.parse(savedData));
        } catch (error) {
          console.error('Error loading package customization:', error);
        }
      }
    }
  }, [packageItem.customization]);

  const formatFeatureText = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>')
      .replace(/•/g, '<span class="bullet-point">•</span>');
  };

  const getProcessSteps = () => {
    if (customizationData?.processSteps) {
      return customizationData.processSteps.sort((a: any, b: any) => a.number - b.number);
    }
    return [
      { id: '1', number: 1, title: 'Initial Consultation', description: 'I\'ll discuss your vision, preferences, and specific requirements to tailor the perfect session for you.' },
      { id: '2', number: 2, title: 'Planning & Preparation', description: 'I\'ll provide guidance on wardrobe, location, timing, and any special considerations for your session.' },
      { id: '3', number: 3, title: 'Photography Session', description: 'Professional photography with natural posing guidance and a relaxed, enjoyable atmosphere.' },
      { id: '4', number: 4, title: 'Editing & Delivery', description: 'Careful editing of your images with professional retouching and delivery via online gallery.' }
    ];
  };

  const getExpectations = () => {
    if (customizationData?.expectations) {
      return customizationData.expectations;
    }
    return [
      { id: '1', icon: 'fas fa-camera', title: 'Professional Equipment', description: 'State-of-the-art camera gear and professional lighting equipment for stunning results.' },
      { id: '2', icon: 'fas fa-heart', title: 'Personal Touch', description: 'Every session is tailored to your unique style and personality for authentic, meaningful photos.' },
      { id: '3', icon: 'fas fa-clock', title: 'Flexible Timing', description: 'I work around your schedule to find the perfect time and lighting for your session.' },
      { id: '4', icon: 'fas fa-star', title: 'Quality Guarantee', description: 'Professional editing and retouching to ensure you receive only the highest quality images.' }
    ];
  };

  const getTestimonials = () => {
    if (customizationData?.testimonials) {
      return customizationData.testimonials;
    }
    return [
      { id: '1', content: 'Carmen has an incredible eye for detail and made our session so comfortable and fun. The photos exceeded our expectations!', author: 'Sarah M.', service: 'Portrait Package' },
      { id: '2', content: 'Professional, creative, and so easy to work with. Carmen captured our family perfectly and we\'ll treasure these photos forever.', author: 'John & Lisa', service: 'Family Package' }
    ];
  };

  const getBookingNote = () => {
    return customizationData?.bookingNote || 'Free consultation included • Flexible scheduling • Professional results guaranteed';
  };

  const price = packageItem.isSpecial && packageItem.specialPrice ? packageItem.specialPrice : packageItem.price;
  const hasDiscount = Boolean(packageItem.isSpecial && packageItem.specialPrice && packageItem.price && packageItem.specialPrice < packageItem.price);

  return (
    <div className="package-preview">
      <div className="preview-header">
        <h3>Live Preview</h3>
        <p>How your package will appear to customers</p>
      </div>
      
      <div className="preview-content">
        {/* Header */}
        <div className="preview-section">
          <div className="preview-package-header">
            <div className="preview-badge-section">
              {packageItem.badge && (
                <div className={`package-badge ${packageItem.badge.toLowerCase()}`}>
                  <span>{packageItem.badge}</span>
                </div>
              )}
              <h4 className="preview-package-title">{packageItem.name || 'Package Name'}</h4>
            </div>
            <div className="preview-package-meta">
              {packageItem.durationMinutes && (
                <div className="duration-info">
                  <i className="fas fa-clock"></i>
                  <span>{packageItem.durationMinutes} minutes</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hero Image */}
        {packageItem.image && (
          <div className="preview-section">
            <div className="preview-hero-image">
              <img src={packageItem.image} alt={packageItem.name || 'Package'} />
              <div className="preview-image-overlay">
                <div className="preview-price-display">
                  {hasDiscount && (
                    <span className="preview-original-price">
                      {packageItem.currency || 'R'} {packageItem.price?.toFixed(0)}
                    </span>
                  )}
                  <span className="preview-current-price">
                    {packageItem.currency || 'R'} {price?.toFixed(0)}
                    {packageItem.isSpecial && <span className="preview-special-badge">Special</span>}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="preview-content-grid">
          {/* Main Content */}
          <div className="preview-main-content">
            {/* Description */}
            {packageItem.short && (
              <div className="preview-content-section">
                <h5>Overview</h5>
                <p className="preview-package-overview">{packageItem.short}</p>
              </div>
            )}

            {packageItem.description && (
              <div className="preview-content-section">
                <h5>What's Included</h5>
                <p className="preview-package-description">{packageItem.description}</p>
              </div>
            )}

            {/* Features */}
            {packageItem.features && packageItem.features.length > 0 && (
              <div className="preview-content-section">
                <h5>Features & Benefits</h5>
                <ul className="preview-features-list">
                  {packageItem.features.map((feature, index) => (
                    <li key={index}>
                      <i className="fas fa-check-circle"></i>
                      <div className="preview-feature-content" dangerouslySetInnerHTML={{ __html: formatFeatureText(feature) }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Process Steps */}
            <div className="preview-content-section">
              <h5>Process</h5>
              <div className="preview-process-steps">
                {getProcessSteps().slice(0, 2).map((step: any, index: number) => (
                  <div key={step.id} className="preview-process-step">
                    <div className="preview-step-number">{index + 1}</div>
                    <div className="preview-step-content">
                      <h6>{step.title}</h6>
                      <p>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expectations */}
            <div className="preview-content-section">
              <h5>What to Expect</h5>
              <div className="preview-expectation-grid">
                {getExpectations().slice(0, 2).map((expectation: any) => (
                  <div key={expectation.id} className="preview-expectation-item">
                    <i className={expectation.icon}></i>
                    <h6>{expectation.title}</h6>
                    <p>{expectation.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="preview-sidebar">
            {/* Testimonials */}
            <div className="preview-testimonials-card">
              <h5>What Clients Say</h5>
              {getTestimonials().slice(0, 1).map((testimonial: any) => (
                <div key={testimonial.id} className="preview-testimonial-item">
                  <div className="preview-testimonial-content">
                    <p>"{testimonial.content}"</p>
                  </div>
                  <div className="preview-testimonial-author">
                    <strong>{testimonial.author}</strong>
                    <span>{testimonial.service}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="preview-cta-section">
              <div className="preview-booking-note">
                <i className="fas fa-info-circle"></i>
                <span>{getBookingNote()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EditState extends Partial<Package> {
  isNew?: boolean;
}

const blankPackage = (): EditState => ({
  isNew: true,
  id: `pkg-${Date.now()}`,
  name: '',
  slug: '',
  category: 'photography',
  short: '',
  description: '',
  image: '',
  price: undefined,
  currency: 'R', // Base currency - foreign visitors see auto-converted prices
  featured: false,
  available: true,
  order: 999,
  features: [],
  heroImage: undefined,
  customization: {
    showProcessSteps: true,
    showExpectations: true,
    showTestimonials: true,
    customBookingNote: '',
    selectedProcessStepIds: [],
    selectedExpectationIds: [],
    selectedTestimonialIds: []
  }
});

const PackageManagement: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [customizationOptions, setCustomizationOptions] = useState<any>(null);
  const [categoryOptions, setCategoryOptions] = useState<{value: string; label: string; color: string}[]>([]);
  const [galleryCategories, setGalleryCategories] = useState<Category[]>([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editingPackageForWizard, setEditingPackageForWizard] = useState<Package | null>(null);
  const justImportedRef = useRef(false);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const isInternalUpdateRef = useRef(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Filter states
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterDuration, setFilterDuration] = useState<string>('all');
  const [filterGroupSize, setFilterGroupSize] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('order');
  const [editingField, setEditingField] = useState<{ packageId: string; field: string } | null>(null);
  const [showHeroImageBrowser, setShowHeroImageBrowser] = useState(false);
  
  const toSlug = (val: string) => (val || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  // Debug log to confirm component is loading
  console.log('🎁 PackageManagement component loaded - v2.0');

  // Load gallery categories (packages now use gallery categories)
  const loadCategoryOptions = async () => {
    try {
      // Load gallery categories from server
      const categories = await loadCategoriesFromServer();
      setGalleryCategories(categories);
      
      // Convert to dropdown options format
      const options = categories.map(cat => ({
        value: cat.slug,
        label: cat.name,
        color: cat.color
      }));
      
      setCategoryOptions(options);
      
      // If no categories exist, add a default "Photography" category
      if (options.length === 0) {
        setCategoryOptions([{ value: 'photography', label: 'Photography', color: '#8B4513' }]);
      }
    } catch (error) {
      console.error('Error loading gallery categories:', error);
      // Fallback to default category
      setCategoryOptions([{ value: 'photography', label: 'Photography', color: '#8B4513' }]);
    }
  };


  // Handle image upload - Gallery style
  const handleImageUpload = (images: CloudinaryImage[]) => {
    console.log('🎁 handleImageUpload called with:', images);
    console.log('🎁 Current editing state:', editing);
    console.log('🎁 Images received:', images?.length, 'images');
    
    if (images && images.length > 0) {
      console.log('🎁 Processing single image for upload...');
      console.log('🎁 Image details:', images[0]);
      
      const newImage = images[0];
      const newImageUrl = newImage?.secure_url || newImage?.url || '';
      
      console.log('🎁 New image URL:', newImageUrl);
      
      setEditing(prev => {
        const updated = {
          ...prev,
          heroImage: newImage,
          image: newImageUrl
        };
        console.log('🎁 Updated editing state:', updated);
        return updated;
      });
      console.log('🎁 Image upload completed successfully');
    } else {
      console.warn('🎁 No images provided to handleImageUpload');
    }
  };

  // Helper to read global customization from localStorage with fallback
  const getGlobalCustomization = (): any => customizationOptions || {};

  const loadCustomizationOptions = async () => {
    try {
      // Prefer server configuration
      const res = await fetch('/api/service-customization', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setCustomizationOptions(data);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch customization from server, falling back to localStorage');
    }
    try {
      const raw = localStorage.getItem('package_customization') || localStorage.getItem('service_customization') || '{}';
      setCustomizationOptions(JSON.parse(raw));
    } catch (e) {
      setCustomizationOptions({});
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.package-info-tooltip') && !target.closest('.package-info-btn')) {
        setOpenTooltipId(null);
      }
    };
    if (openTooltipId) {
      document.addEventListener('mousedown', handleClickOutside);
      // Close on ESC key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setOpenTooltipId(null);
        }
      };
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [openTooltipId]);

  // Prevent Escape key from closing modal
  useEffect(() => {
    if (!editing) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [editing]);

  // Scroll to top when modal opens or editing changes
  useEffect(() => {
    if (editing && modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [editing]);

  // Listen for category updates from gallery category management
  useEffect(() => {
    const handleCategoryUpdate = () => {
      loadCategoryOptions();
    };

    window.addEventListener('categories_updated', handleCategoryUpdate);
    window.addEventListener('storage', handleCategoryUpdate);

    return () => {
      window.removeEventListener('categories_updated', handleCategoryUpdate);
      window.removeEventListener('storage', handleCategoryUpdate);
    };
  }, []);

  useEffect(() => {
    (async () => {
      // If we just imported and already have packages, skip reloading to prevent overwriting
      if (justImportedRef.current && packages.length > 0) {
        console.log('⏸️ Skipping reload - packages just imported and already in state');
        return;
      }
      
      // Load categories first
      await loadCategoryOptions();
      
      const data = await loadServicesFromServer();
      console.log('🎁 Raw package data loaded:', data);

      // Load categories for migration
      const categoriesForMigration = await loadCategoriesFromServer();

      // Migrate old packages: convert single category to categories array
      const migratedData = (data || []).map(pkg => {
        // If package has categories array, use it
        if ((pkg as any).categories && Array.isArray((pkg as any).categories) && (pkg as any).categories.length > 0) {
          return pkg; // Already migrated
        }
        
        // If package has only old category field, migrate it
        if (pkg.category && !((pkg as any).categories && Array.isArray((pkg as any).categories))) {
          // Try to find the category by name or slug
          const category = categoriesForMigration.find(c => 
            c.name === pkg.category || 
            c.slug === pkg.category ||
            c.name.toLowerCase() === pkg.category?.toLowerCase() ||
            c.slug.toLowerCase() === pkg.category?.toLowerCase()
          );
          
          if (category) {
            // Convert to categories array with slug
            return {
              ...pkg,
              categories: [category.slug], // Use slug
              category: undefined // Clear old category field
            };
          } else {
            // If we can't find a match, assume the category value is already a slug
            return {
              ...pkg,
              categories: [pkg.category], // Assume it's a slug
              category: undefined // Clear old category field
            };
          }
        }
        
        return pkg;
      });

      // Save migrated packages if any were migrated
      const needsMigration = migratedData.some((pkg, idx) => {
        const original = data?.[idx];
        return original && (
          (!(original as any).categories || !Array.isArray((original as any).categories) || (original as any).categories.length === 0) &&
          original.category &&
          ((pkg as any).categories && Array.isArray((pkg as any).categories) && (pkg as any).categories.length > 0)
        );
      });

      if (needsMigration) {
        console.log('🔄 Migrating packages from old category format to new categories array...');
        await saveServicesToServer(migratedData);
        console.log('✅ Packages migrated successfully');
      }

      // Initialize missing image data for existing packages
      const initializedData = migratedData.map(pkg => ({
        ...pkg,
        // Initialize heroImage from existing image if not present
        heroImage: pkg.heroImage || (pkg.image ? {
          publicId: pkg.image.split('/').pop()?.split('.')[0] || 'package-image',
          secure_url: pkg.image,
          url: pkg.image,
          filename: 'package-image',
          width: 1200,
          height: 630
        } : undefined)
      }));
      
      console.log('🎁 Initialized package data:', initializedData);
      
      // Sort packages by order field, then by name
      const sortedData = initializedData.sort((a, b) => {
        const orderA = a.order || 999;
        const orderB = b.order || 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
      });
      setPackages(sortedData);
      setLoading(false);
    })();
    loadCustomizationOptions();

    // Reload packages when packages_updated event is dispatched
    // This is useful when packages are updated from other components/tabs
    const handlePackagesUpdated = async () => {
      // Skip reload if this update was triggered internally (by this component)
      if (isInternalUpdateRef.current) {
        isInternalUpdateRef.current = false;
        console.log('⏸️ Skipping reload - internal update');
        return;
      }
      
      console.log('🔄 packages_updated event received from external source, reloading packages...');
      // Only reload if we're not currently saving (to avoid race conditions during import)
      if (!saving) {
        // Save scroll position before reload
        const scrollPosition = tableContainerRef.current?.scrollTop || window.scrollY;
        setLoading(true);
        try {
          const data = await loadServicesFromServer();
          const sortedData = (data || []).sort((a, b) => {
            const orderA = a.order || 999;
            const orderB = b.order || 999;
            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name);
          });
          setPackages(sortedData);
          
          // Restore scroll position after a brief delay to allow render
          setTimeout(() => {
            if (tableContainerRef.current) {
              tableContainerRef.current.scrollTop = scrollPosition;
            } else {
              window.scrollTo(0, scrollPosition);
            }
          }, 0);
        } catch (error) {
          console.error('Error reloading packages:', error);
        } finally {
          setLoading(false);
        }
      } else {
        console.log('⏸️ Skipping reload - save in progress');
      }
    };

    // Refresh when admin customization is saved elsewhere
    const onCustomizationUpdated = () => loadCustomizationOptions();
    window.addEventListener('service_customization_updated', onCustomizationUpdated);
    window.addEventListener('focus', onCustomizationUpdated);
    window.addEventListener('packages_updated', handlePackagesUpdated);
    return () => {
      window.removeEventListener('service_customization_updated', onCustomizationUpdated);
      window.removeEventListener('focus', onCustomizationUpdated);
      window.removeEventListener('packages_updated', handlePackagesUpdated);
    };
  }, []);

  const upsertPackage = async (pkg: EditState) => {
    console.log('🎁 upsertPackage called with:', pkg);
    if (!pkg.name || !pkg.slug) {
      console.error('❌ Missing required fields:', { name: pkg.name, slug: pkg.slug });
      return alert('Name and slug are required');
    }
    
    // Handle categories - only use categories array, derive category from first item for backward compatibility
    let categoryName: string | undefined = undefined;
    let categoriesArray: string[] | undefined = (pkg as any).categories;
    
    // If categories array exists, use it and derive category name from first item for backward compatibility
    if (categoriesArray && Array.isArray(categoriesArray) && categoriesArray.length > 0) {
      // Get the name of the first category for backward compatibility with old code
      const firstCategorySlug = categoriesArray[0];
      const firstCategory = galleryCategories.find(c => c.slug === firstCategorySlug);
      categoryName = firstCategory?.name || firstCategorySlug;
    } else if (pkg.category) {
      // Fallback: if no categories array but has single category, convert it to array
      // Try to find the slug for the category name
      const categoryOption = categoryOptions.find(opt => opt.label === pkg.category || opt.value === pkg.category);
      if (categoryOption) {
        categoriesArray = [categoryOption.value]; // Convert to array with slug
        categoryName = categoryOption.label; // Keep name for backward compatibility
      } else {
        // If we can't find it, just use what we have
        categoryName = pkg.category;
      }
    }
    
    const normalizedSlug = (pkg.slug || '').toLowerCase().replace(/[^a-z0-9-]+/g, '-');
    const updated: Package = {
      id: pkg.id || `pkg-${Date.now()}`,
      name: pkg.name!,
      slug: normalizedSlug,
      category: categoryName, // Only for backward compatibility
      categories: categoriesArray, // Primary: multiple categories array (slugs)
      short: pkg.short?.trim(),
      description: pkg.description?.trim(),
      image: pkg.image?.trim(),
      price: pkg.price,
      currency: (pkg.currency as any) || 'R',
      specialPrice: pkg.specialPrice,
      isSpecial: Boolean(pkg.isSpecial),
      badge: pkg.badge,
      featured: Boolean(pkg.featured),
      available: pkg.available !== false,
      forQuoting: pkg.forQuoting === true ? true : pkg.forQuoting === false ? false : undefined,
      features: pkg.features,
      order: pkg.order ?? 999,
      durationMinutes: pkg.durationMinutes,
      basePriceIncluded: (pkg as any).basePriceIncluded,
      addons: pkg.addons || [],
      updatedAt: new Date().toISOString(),
      customization: pkg.customization || undefined,
      groupSize: pkg.groupSize,
      // Add missing image data
      heroImage: pkg.heroImage,
    };
    
    // Debug log for customization
    if (pkg.customization) {
      console.log('🎁 Saving package customization:', pkg.name, pkg.customization);
    }
    
    let next = [...packages];
    let idx = next.findIndex(p => p.id === updated.id);
    if (idx < 0 && normalizedSlug) {
      idx = next.findIndex(p => (p.slug || '').toLowerCase() === normalizedSlug);
    }
    if (idx >= 0) {
      next[idx] = { ...updated, id: next[idx].id };
    } else {
      next.push(updated);
    }
    next = next.filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
    
    console.log('🎁 About to save packages:', next.length, 'packages');
    setSaving(true);
    const ok = await saveServicesToServer(next);
    setSaving(false);
    if (!ok) console.warn('Saved locally (server not reachable)');
    
    // Mark as internal update to prevent reload
    isInternalUpdateRef.current = true;
    setPackages(next.sort((a, b) => (a.order || 999) - (b.order || 999)));
    setEditing(null);
    console.log('📡 Dispatching packages_updated event (internal)');
    window.dispatchEvent(new Event('packages_updated'));
  };

  const deletePackage = async (id: string) => {
    if (!window.confirm('Delete this package?')) return;
    const next = packages.filter(p => p.id !== id);
    setSaving(true);
    const ok = await saveServicesToServer(next);
    setSaving(false);
    if (!ok) console.warn('Saved locally (server not reachable)');
    
    // Mark as internal update to prevent reload
    isInternalUpdateRef.current = true;
    setPackages(next);
    window.dispatchEvent(new Event('packages_updated'));
  };

  const duplicatePackage = async (packageToDuplicate: Package) => {
    // Create a new package based on the existing one
    const newId = `pkg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newSlug = `${packageToDuplicate.slug}-copy-${Date.now()}`;
    const newName = `${packageToDuplicate.name} (Copy)`;
    
    const duplicatedPackage: Package = {
      ...packageToDuplicate,
      id: newId,
      slug: newSlug,
      name: newName,
      order: (packages.length + 1) * 10, // Place at the end
      updatedAt: new Date().toISOString(),
      featured: false, // Don't duplicate featured status
      available: packageToDuplicate.available !== false, // Default to available
    };

    const next = [...packages, duplicatedPackage];
    setSaving(true);
    const ok = await saveServicesToServer(next);
    setSaving(false);
    if (!ok) console.warn('Saved locally (server not reachable)');
    
    // Mark as internal update to prevent reload
    isInternalUpdateRef.current = true;
    setPackages(next.sort((a, b) => (a.order || 999) - (b.order || 999)));
    window.dispatchEvent(new Event('packages_updated'));
    
    // Show success message
    alert(`Package "${newName}" has been duplicated successfully!`);
  };

  const deleteAllPackages = async () => {
    const count = packages.length;
    if (count === 0) {
      alert('No packages to delete.');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete ALL ${count} packages?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    setSaving(true);
    const ok = await saveServicesToServer([]);
    setSaving(false);
    if (!ok) console.warn('Saved locally (server not reachable)');
    
    // Mark as internal update to prevent reload
    isInternalUpdateRef.current = true;
    setPackages([]);
    window.dispatchEvent(new Event('packages_updated'));
    alert(`Successfully deleted all ${count} packages.`);
  };

  // Helper to escape CSV fields
  const escapeCSV = (value: any): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(';') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Helper to format duration from minutes
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes < 1440) {
      const hours = minutes / 60;
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    const days = minutes / 1440;
    return days === 1 ? '1 day' : `${days} days`;
  };

  const exportTemplates = () => {
    try {
      // CSV Header
      const headers = [
        'Category',
        'Package Name',
        'Short Description',
        'Full Description',
        'Base Price (ZAR)',
        'Currency',
        'Duration (minutes)',
        'Group Size (PAX)',
        'Add-ons (examples)',
        'Badge',
        'Special Price (ZAR)',
        'Is Special',
        'Featured',
        'Available'
      ];

      // Convert actual packages to CSV rows (not templates, so we get featured/available status)
      const rows = packages.map(pkg => {
        const addonsStr = (pkg.addons || []).map(a => a.name).join('; ');
        const basePrice = pkg.price || 0;
        const groupSize = pkg.groupSize || '';
        // Get category name from first category slug if available
        const categoryName = pkg.categories && pkg.categories.length > 0
          ? (() => {
              const firstCategorySlug = pkg.categories[0];
              const matchedCategory = galleryCategories.find(c => c.slug === firstCategorySlug);
              return matchedCategory?.name || pkg.category || firstCategorySlug;
            })()
          : (pkg.category || '');

        return [
          escapeCSV(categoryName),
          escapeCSV(pkg.name),
          escapeCSV(pkg.short || ''),
          escapeCSV(pkg.description || ''),
          escapeCSV(basePrice),
          escapeCSV(pkg.currency || 'R'),
          escapeCSV(pkg.durationMinutes || 0),
          escapeCSV(groupSize),
          escapeCSV(addonsStr),
          escapeCSV(pkg.badge || ''),
          escapeCSV(pkg.specialPrice || ''),
          escapeCSV(pkg.isSpecial ? 'Yes' : 'No'),
          escapeCSV(pkg.featured ? 'Yes' : 'No'),
          escapeCSV(pkg.available !== false ? 'Yes' : 'No')
        ];
      });

      // Combine header and rows
      const csvContent = [
        headers.join(';'),
        ...rows.map(row => row.join(';'))
      ].join('\n');

      // Add BOM for Excel UTF-8 support
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `package-templates-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert(`Successfully exported ${packages.length} packages to CSV file.`);
    } catch (error) {
      console.error('Error exporting templates:', error);
      alert('Failed to export templates. Please try again.');
    }
  };

  // Helper to parse CSV line (handles quoted fields)
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ';' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Helper to parse duration string to minutes
  const parseDurationFromString = (durationStr: string): number => {
    const trimmed = durationStr.trim();
    
    // First, try to parse as a number (raw minutes)
    const numericValue = parseFloat(trimmed);
    if (!isNaN(numericValue) && numericValue >= 0) {
      return Math.round(numericValue);
    }
    
    // If not numeric, try to parse as formatted string
    const lower = trimmed.toLowerCase();
    
    if (lower.includes('session')) {
      const match = lower.match(/(\d+)\s*sessions?/);
      if (match) return parseInt(match[1]) * 120;
    }
    
    if (lower.includes('day')) {
      const match = lower.match(/(\d+(?:\.\d+)?)\s*days?/);
      if (match) return Math.round(parseFloat(match[1]) * 24 * 60);
    }
    
    if (lower.includes('hour')) {
      const match = lower.match(/(\d+(?:\.\d+)?)\s*hours?/);
      if (match) return Math.round(parseFloat(match[1]) * 60);
    }
    
    if (lower.includes('minute')) {
      const match = lower.match(/(\d+)\s*minutes?/);
      if (match) return parseInt(match[1]);
    }
    
    return 0;
  };

  const importTemplatesFromFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row.');
      }

      // Parse header
      const headers = parseCSVLine(lines[0]);
      const headerMap: { [key: string]: number } = {};
      headers.forEach((h, i) => {
        headerMap[h.toLowerCase()] = i;
      });

      // Find column indices
      const getCol = (name: string): number => {
        const idx = headerMap[name.toLowerCase()];
        if (idx === undefined) throw new Error(`Missing required column: ${name}`);
        return idx;
      };

      const categoryIdx = getCol('Category');
      const nameIdx = getCol('Package Name');
      const shortIdx = getCol('Short Description');
      const fullDescIdx = headerMap['full description'] ?? -1;
      const priceIdx = getCol('Base Price (ZAR)');
      const currencyIdx = headerMap['currency'] ?? -1;
      const durationIdx = headerMap['base duration'] ?? headerMap['duration (minutes)'] ?? headerMap['duration'] ?? -1;
      const groupSizeIdx = headerMap['group size (pax)'] ?? headerMap['group size'] ?? headerMap['pax'] ?? -1;
      const addonsIdx = headerMap['add-ons (examples)'] ?? headerMap['addons'] ?? -1;
      const badgeIdx = headerMap['badge'] ?? -1;
      const specialPriceIdx = headerMap['special price (zar)'] ?? headerMap['special price'] ?? -1;
      const isSpecialIdx = headerMap['is special'] ?? -1;
      const featuredIdx = headerMap['featured'] ?? -1;
      const availableIdx = headerMap['available'] ?? -1;

      // Parse data rows
      const importedTemplates: any[] = [];
      for (let i = 1; i < lines.length; i++) {
        const row = parseCSVLine(lines[i]);
        if (row.length < headers.length) continue; // Skip incomplete rows

        const category = row[categoryIdx]?.trim();
        const name = row[nameIdx]?.trim();
        const priceStr = row[priceIdx]?.trim();

        if (!category || !name || !priceStr) continue; // Skip invalid rows

        const basePrice = parseFloat(priceStr);
        if (isNaN(basePrice)) continue;

        // Parse group size using helper function
        const groupSizeStr = groupSizeIdx >= 0 ? row[groupSizeIdx]?.trim() : '';
        const groupSize = parseGroupSize(groupSizeStr);

        // Parse addons
        const addonsStr = addonsIdx >= 0 ? row[addonsIdx]?.trim() : '';
        const addons = addonsStr ? addonsStr.split(';').map(a => ({ name: a.trim() })) : [];

        // Parse boolean fields
        const featuredStr = featuredIdx >= 0 ? row[featuredIdx]?.trim().toLowerCase() : '';
        const featured = featuredStr === 'yes' || featuredStr === 'true' || featuredStr === '1';
        
        const availableStr = availableIdx >= 0 ? row[availableIdx]?.trim().toLowerCase() : '';
        const available = availableStr === '' || availableStr === 'yes' || availableStr === 'true' || availableStr === '1'; // Default to true if empty

        importedTemplates.push({
          name,
          category,
          short: row[shortIdx]?.trim() || '',
          description: fullDescIdx >= 0 ? (row[fullDescIdx]?.trim() || '') : (row[shortIdx]?.trim() || ''),
          price: basePrice,
          currency: currencyIdx >= 0 ? (row[currencyIdx]?.trim() || 'R') : 'R',
          durationMinutes: parseDurationFromString(row[durationIdx]?.trim() || ''),
          groupSize,
          addons,
          badge: badgeIdx >= 0 ? (row[badgeIdx]?.trim() || undefined) : undefined,
          specialPrice: specialPriceIdx >= 0 ? (row[specialPriceIdx]?.trim() ? parseFloat(row[specialPriceIdx].trim()) : undefined) : undefined,
          isSpecial: isSpecialIdx >= 0 ? (row[isSpecialIdx]?.trim().toLowerCase() === 'yes') : false,
          featured,
          available
        });
      }

      if (importedTemplates.length === 0) {
        throw new Error('No valid templates found in the CSV file.');
      }

      if (packages.length > 0) {
        const existingCount = packages.length;
        const message = `This will import ${importedTemplates.length} package templates from file.\n\n` +
          `You currently have ${existingCount} packages.\n\n` +
          `Existing packages with matching names will be UPDATED (images and customizations will be preserved).\n` +
          `New packages will be ADDED.\n\n` +
          `Continue?`;
        if (!window.confirm(message)) {
          event.target.value = '';
          return;
        }
      }

      // Create a map of existing packages by slug for quick lookup
      const existingPackagesBySlug = new Map<string, Package>();
      const existingPackagesByName = new Map<string, Package>();
      packages.forEach(pkg => {
        existingPackagesBySlug.set(pkg.slug.toLowerCase(), pkg);
        existingPackagesByName.set(pkg.name.toLowerCase().trim(), pkg);
      });

      const updatedPackages: Package[] = [];
      const newPackages: Package[] = [];
      const updatedSlugs = new Set<string>();

      importedTemplates.forEach((template: any, index: number) => {
        const slug = template.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const nameLower = template.name.toLowerCase().trim();
        
        // Check if package exists by name or slug
        const existingByName = existingPackagesByName.get(nameLower);
        const existingBySlug = existingPackagesBySlug.get(slug.toLowerCase());
        const existing = existingByName || existingBySlug;

        // Convert template category to category slug(s)
        let categoriesArray: string[] | undefined = undefined;
        let categoryName: string | undefined = undefined;
        
        if (template.category) {
          // Try to find matching category by name or slug (case-insensitive)
          const matchedCategory = galleryCategories.find(c => 
            c.name.toLowerCase() === template.category?.toLowerCase() ||
            c.slug.toLowerCase() === template.category?.toLowerCase()
          );
          
          if (matchedCategory) {
            // Found existing category - use its slug
            categoriesArray = [matchedCategory.slug];
            categoryName = matchedCategory.name;
          } else {
            // Category doesn't exist - generate slug from name
            const generatedSlug = template.category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            categoriesArray = [generatedSlug];
            categoryName = template.category;
          }
        }

        if (existing) {
          // UPDATE existing package - preserve images and customizations
          const updatedPackage: Package = {
            ...existing,
            name: template.name,
            category: categoryName, // For backward compatibility
            categories: categoriesArray, // Primary: array of category slugs
            short: template.short || existing.short || '',
            description: template.description || existing.description || '',
            // Preserve images - don't overwrite with empty values
            // image, heroImage, and galleryImages are preserved from existing
            price: template.price,
            currency: template.currency || existing.currency || 'R',
            specialPrice: template.specialPrice,
            isSpecial: template.isSpecial || false,
            badge: template.badge || existing.badge,
            durationMinutes: template.durationMinutes,
            groupSize: template.groupSize !== undefined ? template.groupSize : existing.groupSize,
            addons: template.addons || [],
            // Update featured and available from template if provided
            featured: template.featured !== undefined ? template.featured : existing.featured,
            available: template.available !== undefined ? template.available : existing.available,
            // Preserve customization
            customization: existing.customization,
            updatedAt: new Date().toISOString(),
          };
          updatedPackages.push(updatedPackage);
          updatedSlugs.add(existing.slug.toLowerCase());
        } else {
          // CREATE new package
          let finalSlug = slug;
          let counter = 1;
          
          while (existingPackagesBySlug.has(finalSlug.toLowerCase()) || updatedSlugs.has(finalSlug.toLowerCase())) {
            finalSlug = `${slug}-${counter}`;
            counter++;
          }
          updatedSlugs.add(finalSlug.toLowerCase());

          const newPackage: Package = {
            id: `pkg-${Date.now()}-${index}`,
            name: template.name,
            slug: finalSlug,
            category: categoryName, // For backward compatibility
            categories: categoriesArray, // Primary: array of category slugs
            short: template.short || '',
            description: template.description || '',
            image: '',
            price: template.price,
            currency: template.currency || 'R',
            specialPrice: template.specialPrice,
            isSpecial: template.isSpecial || false,
            badge: template.badge,
            featured: template.featured !== undefined ? template.featured : false,
            available: template.available !== undefined ? template.available : true,
            order: (packages.length + newPackages.length + 1) * 10,
            durationMinutes: template.durationMinutes,
            groupSize: template.groupSize,
            addons: template.addons || [],
            updatedAt: new Date().toISOString(),
          };
          newPackages.push(newPackage);
        }
      });

      // Merge: update existing packages, keep unchanged ones, add new ones
      const combinedPackages = packages.map(pkg => {
        const updated = updatedPackages.find(up => up.id === pkg.id);
        return updated || pkg;
      }).concat(newPackages);
      setSaving(true);
      const ok = await saveServicesToServer(combinedPackages);
      setSaving(false);
      if (!ok) console.warn('Saved locally (server not reachable)');
      
      const sorted = combinedPackages.sort((a, b) => (a.order || 999) - (b.order || 999));
      console.log('📦 Imported packages:', sorted.length, 'total packages');
      console.log('📦 New packages:', newPackages.length);
      console.log('📦 Updated packages:', updatedPackages.length);
      
      // Mark that we just imported to prevent reload from overwriting
      justImportedRef.current = true;
      setPackages(sorted);
      
      // Reset filters to show all packages
      setFilterCategory('all');
      setFilterDuration('all');
      setFilterGroupSize('all');
      
      // Clear the flag after a longer delay to allow component to stabilize
      setTimeout(() => {
        justImportedRef.current = false;
        console.log('✅ Import guard cleared');
      }, 5000);
      
      const updateCount = updatedPackages.length;
      const newCount = newPackages.length;
      const message = updateCount > 0 && newCount > 0
        ? `Successfully imported ${importedTemplates.length} packages: ${updateCount} updated, ${newCount} new packages added!`
        : updateCount > 0
        ? `Successfully updated ${updateCount} existing packages!`
        : `Successfully imported ${newCount} new package templates from CSV file!`;
      alert(message);
      
      event.target.value = '';
    } catch (error: any) {
      console.error('Error importing templates:', error);
      alert(`Failed to import templates: ${error.message || 'Invalid file format'}`);
      event.target.value = '';
    }
  };

  const importAllTemplates = async () => {
    if (packages.length > 0) {
      const existingCount = packages.length;
      const templateCount = PACKAGE_TEMPLATES.length;
      const message = `This will import ${templateCount} package templates.\n\n` +
        `You currently have ${existingCount} packages.\n\n` +
        `Existing packages with matching names will be UPDATED (images and customizations will be preserved).\n` +
        `New packages will be ADDED.\n\n` +
        `Continue?`;
      if (!window.confirm(message)) return;
    }

    // Load categories to match template categories to slugs
    const categoriesForImport = await loadCategoriesFromServer();
    
    // Create maps for existing packages by name and slug
    const existingPackagesBySlug = new Map<string, Package>();
    const existingPackagesByName = new Map<string, Package>();
    packages.forEach(pkg => {
      existingPackagesBySlug.set(pkg.slug.toLowerCase(), pkg);
      existingPackagesByName.set(pkg.name.toLowerCase().trim(), pkg);
    });
    
    const updatedPackages: Package[] = [];
    const newPackages: Package[] = [];
    const updatedSlugs = new Set<string>();
    
    PACKAGE_TEMPLATES.forEach((template, index) => {
      const slug = template.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const nameLower = template.name.toLowerCase().trim();
      
      // Check if package exists by name or slug
      const existingByName = existingPackagesByName.get(nameLower);
      const existingBySlug = existingPackagesBySlug.get(slug.toLowerCase());
      const existing = existingByName || existingBySlug;

      // Convert template category to category slug(s)
      let categoriesArray: string[] | undefined = undefined;
      let categoryName: string | undefined = undefined;
      
      if (template.category) {
        // Try to find matching category by name or slug (case-insensitive)
        const matchedCategory = categoriesForImport.find(c => 
          c.name.toLowerCase() === template.category?.toLowerCase() ||
          c.slug.toLowerCase() === template.category?.toLowerCase()
        );
        
        if (matchedCategory) {
          // Found existing category - use its slug
          categoriesArray = [matchedCategory.slug];
          categoryName = matchedCategory.name;
        } else {
          // Category doesn't exist - generate slug from name
          const generatedSlug = template.category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          categoriesArray = [generatedSlug];
          categoryName = template.category;
        }
      }

      if (existing) {
        // UPDATE existing package - preserve images and customizations
        const updatedPackage: Package = {
          ...existing,
          name: template.name,
          category: categoryName, // For backward compatibility
          categories: categoriesArray, // Primary: array of category slugs
          short: template.short || existing.short || '',
          description: template.description || existing.description || '',
          // Preserve images - don't overwrite with empty values
          // image, heroImage, and galleryImages are preserved from existing
          price: template.price,
          currency: template.currency || existing.currency || 'R',
          specialPrice: template.specialPrice,
          isSpecial: template.isSpecial || false,
          badge: template.badge || existing.badge,
          durationMinutes: template.durationMinutes,
          groupSize: template.groupSize !== undefined ? template.groupSize : existing.groupSize,
          addons: template.addons || [],
          // Preserve customization
          customization: existing.customization,
          updatedAt: new Date().toISOString(),
        };
        updatedPackages.push(updatedPackage);
        updatedSlugs.add(existing.slug.toLowerCase());
      } else {
        // CREATE new package
        let finalSlug = slug;
        let counter = 1;
        
        while (existingPackagesBySlug.has(finalSlug.toLowerCase()) || updatedSlugs.has(finalSlug.toLowerCase())) {
          finalSlug = `${slug}-${counter}`;
          counter++;
        }
        updatedSlugs.add(finalSlug.toLowerCase());

        const newPackage: Package = {
          id: `pkg-${Date.now()}-${index}`,
          name: template.name,
          slug: finalSlug,
          category: categoryName, // For backward compatibility
          categories: categoriesArray, // Primary: array of category slugs
          short: template.short || '',
          description: template.description || '',
          image: '',
          price: template.price,
          currency: template.currency || 'R',
          specialPrice: template.specialPrice,
          isSpecial: template.isSpecial || false,
          badge: template.badge,
          featured: false,
          available: true,
          order: (packages.length + newPackages.length + 1) * 10,
          durationMinutes: template.durationMinutes,
          addons: template.addons || [],
          updatedAt: new Date().toISOString(),
        };
        newPackages.push(newPackage);
      }
    });

    // Merge: update existing packages, keep unchanged ones, add new ones
    const combinedPackages = packages.map(pkg => {
      const updated = updatedPackages.find(up => up.id === pkg.id);
      return updated || pkg;
    }).concat(newPackages);
    setSaving(true);
    const ok = await saveServicesToServer(combinedPackages);
    setSaving(false);
    if (!ok) console.warn('Saved locally (server not reachable)');
    
    // Sort by order
    const sorted = combinedPackages.sort((a, b) => (a.order || 999) - (b.order || 999));
    console.log('📦 Imported from templates:', sorted.length, 'total packages');
    console.log('📦 New packages:', newPackages.length);
    console.log('📦 Updated packages:', updatedPackages.length);
    
    // Mark that we just imported to prevent reload from overwriting
    justImportedRef.current = true;
    setPackages(sorted);
    
    // Reset filters to show all packages
    setFilterCategory('all');
    setFilterDuration('all');
    setFilterGroupSize('all');
    
    // Clear the flag after a longer delay to allow component to stabilize
    setTimeout(() => {
      justImportedRef.current = false;
      console.log('✅ Import guard cleared');
    }, 5000);
    
    const updateCount = updatedPackages.length;
    const newCount = newPackages.length;
    const message = updateCount > 0 && newCount > 0
      ? `Successfully imported ${PACKAGE_TEMPLATES.length} templates:\n\n${updateCount} existing packages UPDATED (images preserved)\n${newCount} new packages ADDED`
      : updateCount > 0
      ? `Successfully updated ${updateCount} existing packages!\n\nAll images and customizations have been preserved.`
      : `Successfully imported ${newCount} new package templates!`;
    alert(message);
  };

  const featuredCount = useMemo(() => packages.filter(p => p.featured).length, [packages]);

  // Filter packages based on selected filters
  const filteredPackages = useMemo(() => {
    let filtered = [...packages];

    // Filter by category
    if (filterCategory !== 'all') {
      // Find the category option to get the name
      const selectedCategoryOption = categoryOptions.find(opt => opt.value === filterCategory);
      const categoryNameToMatch = selectedCategoryOption?.label || filterCategory;
      
      filtered = filtered.filter(p => {
        const filterCategoryName = categoryNameToMatch.toLowerCase().trim();
        const filterCategorySlug = filterCategory.toLowerCase().trim();
        
        // Check multiple categories array first
        if ((p as any).categories && Array.isArray((p as any).categories) && (p as any).categories.length > 0) {
          return (p as any).categories.some((categorySlug: string) => {
            const category = galleryCategories.find((c: Category) => c.slug === categorySlug);
            const categoryName = category?.name.toLowerCase().trim() || '';
            const categorySlugLower = categorySlug.toLowerCase().trim();
            
            return categoryName === filterCategoryName || 
                   categorySlugLower === filterCategorySlug ||
                   categoryName === filterCategorySlug ||
                   categorySlugLower === filterCategoryName;
          });
        }
        
        // Fallback to single category field
        if (!p.category) return false;
        const packageCategory = p.category.toLowerCase().trim();
        
        // Match by name or slug
        return packageCategory === filterCategoryName || packageCategory === filterCategorySlug;
      });
    }

    // Filter by duration range
    if (filterDuration !== 'all') {
      filtered = filtered.filter(p => {
        if (!p.durationMinutes) return false;
        const duration = p.durationMinutes;
        switch (filterDuration) {
          case 'quick':
            return duration >= 0 && duration <= 120;
          case 'standard':
            return duration >= 121 && duration <= 240;
          case 'extended':
            return duration >= 241 && duration <= 360;
          case 'halfday':
            return duration >= 240 && duration <= 240;
          case 'fullday':
            return duration >= 480;
          default:
            return true;
        }
      });
    }

    // Filter by group size (PAX)
    if (filterGroupSize !== 'all') {
      filtered = filtered.filter(p => {
        if (!p.groupSize) return false;
        return p.groupSize.toLowerCase() === filterGroupSize.toLowerCase();
      });
    }

    // Helper function to get category name for sorting
    const getCategoryName = (pkg: Package): string => {
      if ((pkg as any).categories && Array.isArray((pkg as any).categories) && (pkg as any).categories.length > 0) {
        const firstCategorySlug = (pkg as any).categories[0];
        const category = galleryCategories.find((c: Category) => c.slug === firstCategorySlug);
        return category?.name || firstCategorySlug || '';
      }
      return pkg.category || '';
    };

    // Helper function to get group size order for sorting
    const getGroupSizeOrder = (groupSize?: string): number => {
      if (!groupSize) return 999;
      const size = groupSize.toLowerCase();
      const order: { [key: string]: number } = {
        'solo': 1,
        'small': 2,
        'medium': 3,
        'large': 4,
        'verylarge': 5,
        'any': 6
      };
      return order[size] || 999;
    };

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'category':
          const catA = getCategoryName(a).toLowerCase();
          const catB = getCategoryName(b).toLowerCase();
          return catA.localeCompare(catB);
        
        case 'duration':
          const durA = a.durationMinutes || 0;
          const durB = b.durationMinutes || 0;
          return durA - durB;
        
        case 'groupsize':
          const sizeA = getGroupSizeOrder(a.groupSize);
          const sizeB = getGroupSizeOrder(b.groupSize);
          return sizeA - sizeB;
        
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        
        case 'price':
          const priceA = a.price || 0;
          const priceB = b.price || 0;
          return priceA - priceB;
        
        case 'order':
        default:
          const orderA = a.order || 999;
          const orderB = b.order || 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.name || '').localeCompare(b.name || '');
      }
    });

    return sorted;
  }, [packages, filterCategory, filterDuration, filterGroupSize, sortBy, categoryOptions, galleryCategories]);

  if (loading) return <section className="admin-section"><p>Loading packages...</p></section>;

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>
          <i className="fas fa-gift"></i>
          Package Management
          <span style={{
            marginLeft: 12,
            fontSize: '0.85rem',
            color: '#6C757D',
            border: '1px solid #E1E5E9',
            borderRadius: 8,
            padding: '2px 8px'
          }}>v2.1</span>
        </h2>
        <div className="section-controls">
          <button className="btn btn-primary" onClick={() => setShowWizard(true)}>
            <i className="fas fa-magic"></i>
            Create New Package
          </button>
          <button 
            className="btn btn-info" 
            onClick={exportTemplates}
            title="Export templates to JSON file for editing"
          >
            <i className="fas fa-file-export"></i>
            Export Templates
          </button>
          <label className="btn btn-success import-btn" style={{ cursor: 'pointer', margin: 0 }}>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={importTemplatesFromFile}
              style={{ display: 'none' }}
            />
            <i className="fas fa-file-import"></i>
            <span>Import Excel/CSV</span>
          </label>
          <button 
            className="btn btn-success" 
            onClick={importAllTemplates}
            title={`Import all ${PACKAGE_TEMPLATES.length} package templates`}
          >
            <i className="fas fa-download"></i>
            Import All Templates
          </button>
          {packages.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={deleteAllPackages}
              title="Delete all packages"
            >
              <i className="fas fa-trash-alt"></i>
              Delete All Packages
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => window.location.href = '/packages'}>
            <i className="fas fa-eye"></i>
            View Public Page
          </button>
        </div>
      </div>
      
      <div className="package-stats">
        <div className="stat-card">
          <i className="fas fa-gift"></i>
          <div className="stat-content">
            <span className="stat-number">{filteredPackages.length}</span>
            <span className="stat-label">
              {filteredPackages.length === packages.length ? 'Total Packages' : `Filtered (${packages.length} total)`}
            </span>
          </div>
        </div>
        <div className="stat-card">
          <i className="fas fa-star"></i>
          <div className="stat-content">
            <span className="stat-number">{filteredPackages.filter(p => p.featured).length}</span>
            <span className="stat-label">Featured</span>
          </div>
        </div>
        <div className="stat-card">
          <i className="fas fa-check-circle"></i>
          <div className="stat-content">
            <span className="stat-number">{filteredPackages.filter(p => p.available).length}</span>
            <span className="stat-label">Available</span>
          </div>
        </div>
        {saving && (
          <div className="stat-card saving">
            <i className="fas fa-spinner fa-spin"></i>
            <div className="stat-content">
              <span className="stat-label">Saving...</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Filter Section */}
      <div className="package-filters">
        <div className="filter-group">
          <label htmlFor="category-filter">
            <i className="fas fa-tag"></i>
            Category
          </label>
          <select
            id="category-filter"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-dropdown"
          >
            <option value="all">All Packages ({packages.length})</option>
            {categoryOptions.map(cat => {
              const catName = cat.label.toLowerCase().trim();
              const catSlug = cat.value.toLowerCase().trim();
              
              const categoryCount = packages.filter(p => {
                // Check multiple categories array first
                if ((p as any).categories && Array.isArray((p as any).categories) && (p as any).categories.length > 0) {
                  return (p as any).categories.some((categorySlug: string) => {
                    const category = galleryCategories.find((c: Category) => c.slug === categorySlug);
                    const categoryName = category?.name.toLowerCase().trim() || '';
                    const categorySlugLower = categorySlug.toLowerCase().trim();
                    
                    return categoryName === catName || 
                           categorySlugLower === catSlug ||
                           categoryName === catSlug ||
                           categorySlugLower === catName;
                  });
                }
                
                // Fallback to single category field
                if (!p.category) return false;
                const packageCategory = p.category.toLowerCase().trim();
                return packageCategory === catName || packageCategory === catSlug;
              }).length;
              return (
                <option key={cat.value} value={cat.value}>
                  {cat.label} ({categoryCount})
                </option>
              );
            })}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="filter-duration">
            <i className="fas fa-clock"></i>
            Duration
          </label>
          <select
            id="filter-duration"
            value={filterDuration}
            onChange={(e) => setFilterDuration(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Durations</option>
            <option value="quick">Up to 2 hours</option>
            <option value="standard">{'>'}2 to 4 hours</option>
            <option value="halfday">Half Day [4 hours]</option>
            <option value="extended">{'>'}4 to 6 hours</option>
            <option value="fullday">Full Day [8 hours]</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="filter-groupsize">
            <i className="fas fa-users"></i>
            Group Size (PAX)
          </label>
          <select
            id="filter-groupsize"
            value={filterGroupSize}
            onChange={(e) => setFilterGroupSize(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Sizes</option>
            <option value="solo">Solo / Couple (1-2 people)</option>
            <option value="small">Small Group (3-6 people)</option>
            <option value="medium">Medium Group (up to 12 people)</option>
            <option value="large">Large Group (up to 25 people)</option>
            <option value="verylarge">Very Large Group ({'>'}25 people)</option>
            <option value="any">Any Size (flexible)</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sort-by">
            <i className="fas fa-sort"></i>
            Sort By
          </label>
          <select
            id="sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="order">Default Order</option>
            <option value="name">Name (A-Z)</option>
            <option value="category">Category</option>
            <option value="duration">Duration</option>
            <option value="groupsize">Group Size</option>
            <option value="price">Price</option>
          </select>
        </div>

        {(filterCategory !== 'all' || filterDuration !== 'all' || filterGroupSize !== 'all') && (
          <button
            className="btn btn-secondary btn-clear-filters"
            onClick={() => {
              setFilterCategory('all');
              setFilterDuration('all');
              setFilterGroupSize('all');
            }}
            title="Clear all filters"
          >
            <i className="fas fa-times"></i>
            Clear Filters
          </button>
        )}
      </div>

      {packages.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-content">
            <i className="fas fa-gift"></i>
            <h3>No Packages Yet</h3>
            <p>Create your first package offering to showcase your photography services and attract clients</p>
            <button className="btn btn-primary btn-large" onClick={() => setShowWizard(true)}>
              <i className="fas fa-magic"></i>
              Create Your First Package
            </button>
          </div>
        </div>
      ) : filteredPackages.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-content">
            <i className="fas fa-filter"></i>
            <h3>No Packages Match Filters</h3>
            <p>Try adjusting your filter criteria to see more packages</p>
            <button className="btn btn-secondary" onClick={() => {
              setFilterCategory('all');
              setFilterDuration('all');
              setFilterGroupSize('all');
            }}>
              <i className="fas fa-times"></i>
              Clear Filters
            </button>
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }} ref={tableContainerRef}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Duration</th>
              <th>Price</th>
              <th>Special</th>
              <th>Badge</th>
              <th>Featured</th>
              <th>Available</th>
              <th>Quote</th>
              <th>Actions</th>
            </tr>
          </thead>
            <tbody>
              {filteredPackages.map(p => (
                <tr 
                  key={p.id} 
                  className={`package-row ${(!p.category && (!(p as any).categories || (p as any).categories.length === 0)) ? 'no-category-row' : ''}`} 
                  data-package-id={p.id}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'center', width: '100%' }}>
                      <input 
                        type="number" 
                        value={p.order || 999} 
                        onChange={(e) => {
                          const newOrder = Number(e.target.value);
                          upsertPackage({ ...p, order: newOrder });
                        }}
                        style={{ width: 40 }} 
                        min="1"
                        max="999"
                      />
                      <button
                        className="package-info-btn order-info-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltipId(openTooltipId === p.id ? null : p.id);
                        }}
                        title="View package details"
                      >
                        <i className="fas fa-info-circle"></i>
                      </button>
                    </div>
                  </td>
                <td className="package-image-cell">
                  {p.image ? (
                    <div className="package-image-preview">
                      <img 
                        src={p.image} 
                        alt={p.name}
                        className="package-thumbnail"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="no-image-placeholder" style={{ display: 'none' }}>
                        <i className="fas fa-image"></i>
                      </div>
                    </div>
                  ) : (
                    <div className="no-image-placeholder">
                      <i className="fas fa-image"></i>
                    </div>
                  )}
                </td>
                <td className="package-name-cell">
                  {editingField?.packageId === p.id && editingField?.field === 'name' ? (
                    <input
                      type="text"
                      value={p.name || ''}
                      onChange={(e) => {
                        upsertPackage({ ...p, name: e.target.value });
                      }}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setEditingField(null);
                        } else if (e.key === 'Escape') {
                          setEditingField(null);
                        }
                      }}
                      autoFocus
                      style={{ width: '100%', padding: '4px' }}
                      placeholder="Package name"
                    />
                  ) : (
                    <span
                      onClick={() => setEditingField({ packageId: p.id, field: 'name' })}
                      style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      title="Click to edit name"
                    >
                      {p.name}
                    </span>
                  )}
                </td>
                <td>
                  {(p as any).categories && Array.isArray((p as any).categories) && (p as any).categories.length > 0 ? (
                    <div className="package-categories-display">
                      {(p as any).categories.slice(0, 3).map((categorySlug: string, idx: number) => {
                        const category = galleryCategories.find((c: Category) => c.slug === categorySlug);
                        return (
                          <span 
                            key={idx} 
                            className="package-category-badge" 
                            title={category?.name || categorySlug}
                            style={{ backgroundColor: category?.color || '#8B4513' }}
                          >
                            {category?.name || categorySlug}
                          </span>
                        );
                      })}
                      {(p as any).categories.length > 3 && (
                        <span className="package-category-more" title={`+${(p as any).categories.length - 3} more`}>
                          +{(p as any).categories.length - 3}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="package-no-category" title="No category assigned">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>No Category</span>
                    </span>
                  )}
                </td>
                <td>
                  {editingField?.packageId === p.id && editingField?.field === 'duration' ? (
                    <input
                      type="number"
                      value={p.durationMinutes || ''}
                      onChange={(e) => {
                        const minutes = e.target.value ? parseInt(e.target.value) : undefined;
                        upsertPackage({ ...p, durationMinutes: minutes });
                      }}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setEditingField(null);
                        } else if (e.key === 'Escape') {
                          setEditingField(null);
                        }
                      }}
                      autoFocus
                      style={{ width: '80px', padding: '4px' }}
                      min="0"
                      placeholder="Minutes"
                    />
                  ) : (
                    <span
                      onClick={() => setEditingField({ packageId: p.id, field: 'duration' })}
                      style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      title="Click to edit duration"
                    >
                      {p.durationMinutes ? formatDuration(p.durationMinutes) : '-'}
                    </span>
                  )}
                </td>
                <td>
                  {editingField?.packageId === p.id && editingField?.field === 'price' ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>R</span>
                      <input
                        type="number"
                        value={p.price || ''}
                        onChange={(e) => {
                          const price = e.target.value ? parseFloat(e.target.value) : undefined;
                          upsertPackage({ ...p, price: price, currency: 'R' });
                        }}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setEditingField(null);
                          } else if (e.key === 'Escape') {
                            setEditingField(null);
                          }
                        }}
                        autoFocus
                        style={{ width: '80px', padding: '4px' }}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                  ) : (
                    <span
                      onClick={() => setEditingField({ packageId: p.id, field: 'price' })}
                      style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      title="Click to edit price"
                    >
                      {p.price ? `R ${p.price}` : '-'}
                    </span>
                  )}
                </td>
                <td>
                  {editingField?.packageId === p.id && editingField?.field === 'specialprice' ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>R</span>
                      <input
                        type="number"
                        value={p.specialPrice || ''}
                        onChange={(e) => {
                          const specialPrice = e.target.value ? parseFloat(e.target.value) : undefined;
                          upsertPackage({ 
                            ...p, 
                            specialPrice: specialPrice,
                            isSpecial: specialPrice !== undefined && specialPrice > 0,
                            currency: 'R'
                          });
                        }}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setEditingField(null);
                          } else if (e.key === 'Escape') {
                            setEditingField(null);
                          }
                        }}
                        autoFocus
                        style={{ width: '80px', padding: '4px' }}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                  ) : (
                    <span
                      onClick={() => setEditingField({ packageId: p.id, field: 'specialprice' })}
                      style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      title="Click to edit special price"
                    >
                      {p.isSpecial && p.specialPrice ? `R ${p.specialPrice}` : '-'}
                    </span>
                  )}
                </td>
                <td>{p.badge || '-'}</td>
                <td>
                  <label className="table-toggle table-toggle-star" title={p.featured ? 'Unfeature this package' : 'Feature this package'}>
                    <input
                      type="checkbox"
                      checked={!!p.featured}
                      onChange={(e) => {
                        upsertPackage({ ...p, featured: e.target.checked });
                      }}
                    />
                    <span className="toggle-slider">
                      <i className="fas fa-star"></i>
                    </span>
                  </label>
                </td>
                <td>
                  <label className="table-toggle">
                    <input
                      type="checkbox"
                      checked={p.available !== false}
                      onChange={(e) => {
                        const available = e.target.checked;
                        const wasQuotable = p.forQuoting === true || (p.forQuoting !== false && p.available !== false);
                        upsertPackage({
                          ...p,
                          available,
                          forQuoting: available ? p.forQuoting : (wasQuotable ? true : p.forQuoting),
                        });
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </td>
                <td>
                  <label className="table-toggle" title="Use this package when writing a quotation">
                    <input
                      type="checkbox"
                      checked={p.forQuoting === true || (p.forQuoting !== false && p.available !== false)}
                      onChange={(e) => {
                        upsertPackage({ ...p, forQuoting: e.target.checked });
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </td>
                <td className="package-actions-cell">
                  <div className="action-buttons">
                    <button 
                      className="action-icon-btn action-edit" 
                      onClick={() => {
                        let categorySlug = p.category || 'photography';
                        if (p.category && categoryOptions.length > 0) {
                          const categoryByName = categoryOptions.find(opt => 
                            opt.label.toLowerCase() === p.category?.toLowerCase()
                          );
                          if (categoryByName) {
                            categorySlug = categoryByName.value;
                          } else {
                            const categoryBySlug = categoryOptions.find(opt => 
                              opt.value.toLowerCase() === p.category?.toLowerCase()
                            );
                            if (categoryBySlug) {
                              categorySlug = categoryBySlug.value;
                            }
                          }
                        }
                        const packageToEdit = {
                          ...p,
                          category: categorySlug,
                          heroImage: p.heroImage || (p.image ? {
                            publicId: '',
                            public_id: '',
                            secure_url: p.image,
                            filename: 'package-image',
                            width: 0,
                            height: 0,
                            bytes: 0
                          } : {} as CloudinaryImage)
                        };
                        setEditingPackageForWizard(p);
                        setShowWizard(true);
                      }}
                      title="Edit package"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button 
                      className="action-icon-btn action-duplicate" 
                      onClick={() => duplicatePackage(p)}
                      title="Duplicate package"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                    <button 
                      className="action-icon-btn action-delete" 
                      onClick={() => deletePackage(p.id)}
                      title="Delete package"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Package Info Modal - Rendered via Portal */}
      {openTooltipId && (() => {
        const packageData = filteredPackages.find(p => p.id === openTooltipId);
        if (!packageData) return null;
        
        return createPortal(
          <div className="package-info-modal-overlay" onClick={() => setOpenTooltipId(null)}>
            <div className="package-info-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tooltip-header">
                <h4>{packageData.name}</h4>
                <button
                  className="tooltip-close-btn"
                  onClick={() => setOpenTooltipId(null)}
                  title="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
                {packageData.badge && <span className="tooltip-badge">{packageData.badge}</span>}
              </div>
              <div className="tooltip-body">
                {packageData.short && (
                  <div className="tooltip-section">
                    <strong>Overview:</strong>
                    <p>{packageData.short}</p>
                  </div>
                )}
                {packageData.description && (
                  <div className="tooltip-section">
                    <strong>Description:</strong>
                    <p>{packageData.description}</p>
                  </div>
                )}
                <div className="tooltip-details">
                  <div className="tooltip-detail-item">
                    <i className="fas fa-tag"></i>
                    <span>
                      <strong>Categories:</strong>{' '}
                      {(packageData as any).categories && Array.isArray((packageData as any).categories) && (packageData as any).categories.length > 0 ? (
                        <div className="tooltip-categories-list">
                          {(packageData as any).categories.map((categorySlug: string, idx: number) => {
                            const category = galleryCategories.find((c: Category) => c.slug === categorySlug);
                            return (
                              <span 
                                key={idx} 
                                className="tooltip-category-badge"
                                style={{ backgroundColor: category?.color || '#8B4513' }}
                              >
                                {category?.name || categorySlug}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span>No categories assigned</span>
                      )}
                    </span>
                  </div>
                  {packageData.durationMinutes && (
                    <div className="tooltip-detail-item">
                      <i className="fas fa-clock"></i>
                      <span><strong>Duration:</strong> {packageData.durationMinutes} minutes</span>
                    </div>
                  )}
                  <div className="tooltip-detail-item">
                    <i className="fas fa-money-bill-wave"></i>
                    <span><strong>Price:</strong> {packageData.currency || 'R'} {packageData.price || 'N/A'}</span>
                  </div>
                  {packageData.isSpecial && packageData.specialPrice && (
                    <div className="tooltip-detail-item">
                      <i className="fas fa-star"></i>
                      <span><strong>Special Price:</strong> {packageData.currency || 'R'} {packageData.specialPrice}</span>
                    </div>
                  )}
                  {(packageData as any).addons && (packageData as any).addons.length > 0 && (
                    <div className="tooltip-detail-item">
                      <i className="fas fa-plus-circle"></i>
                      <span><strong>Add-ons:</strong> {(packageData as any).addons.length} available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {editing && createPortal(
        <div className="service-modal-overlay">
          <div className="service-modal" onClick={(e) => e.stopPropagation()}>
                 <div className="service-modal-header">
                   <div className="header-content">
                     <h2>
                       <i className="fas fa-gift"></i>
                       {editing.isNew ? 'Add New Package' : 'Edit Package'}
                     </h2>
                     {!editing.isNew && editing.name && (
                       <div className="package-name-header-display">
                         <span className="package-name-label-header">Package:</span>
                         <span className="package-name-value-header">{editing.name}</span>
                       </div>
                     )}
                     <p className="header-subtitle">
                       {editing.isNew ? 'Create a professional package offering' : 'Update your package details'}
                     </p>
                   </div>
                   <div className="header-actions">
                     <button 
                       className={`preview-toggle-btn ${showPreview ? 'active' : ''}`}
                       onClick={() => setShowPreview(!showPreview)}
                       title={showPreview ? 'Hide Preview' : 'Show Preview'}
                     >
                       <i className={`fas ${showPreview ? 'fa-edit' : 'fa-eye'}`}></i>
                       <span>{showPreview ? 'Edit' : 'Preview'}</span>
                     </button>
                     <button onClick={() => setEditing(null)} className="close-button">
                       <i className="fas fa-times"></i>
                     </button>
                   </div>
                 </div>
            <div className="service-modal-body" ref={modalBodyRef}>
              {showPreview ? (
                <PackagePreview package={editing} />
              ) : (
                <div className="form-sections">
                {/* Basic Information Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-file-alt"></i>
                    </div>
                    <div className="section-title">
                      <h3>Basic Information</h3>
                      <p>Core package details and identification</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    {/* Template selection */}
                    <div className="field-group">
                      <label className="field-label">
                        <span>Start from Template</span>
                        <select
                          value={selectedTemplateKey}
                          onChange={(e) => {
                            const key = e.target.value;
                            setSelectedTemplateKey(key);

                            if (!key) {
                              return;
                            }

                            const tpl = PACKAGE_TEMPLATES.find(t => t.templateKey === key);
                            if (!tpl) {
                              return;
                            }

                            // Apply template values onto current editing state, preserving id/slug/order/customization/heroImage where present.
                            setEditing(prev => {
                              const base = prev || blankPackage();
                              // Deep copy tiers and addons to ensure all properties (including durationMinutes and descriptions) are included
                              const addons = tpl.addons ? tpl.addons.map(addon => ({
                                name: addon.name,
                                price: addon.price
                              })) : [];
                              
                              return {
                                ...base,
                                name: tpl.name,
                                category: tpl.category,
                                short: tpl.short,
                                description: tpl.description,
                                price: tpl.price,
                                currency: tpl.currency,
                                specialPrice: tpl.specialPrice,
                                isSpecial: tpl.isSpecial,
                                badge: tpl.badge,
                                durationMinutes: tpl.durationMinutes,
                                addons: addons,
                              };
                            });
                          }}
                          className="form-select"
                        >
                          <option value="">Start from blank</option>
                          {PACKAGE_TEMPLATES.map(t => (
                            <option key={t.templateKey} value={t.templateKey}>
                              {t.category} – {t.name}
                            </option>
                          ))}
                        </select>
                        <small className="field-hint">
                          Choose a preset package to prefill fields, then tweak details for this specific offer.
                        </small>
                      </label>
                    </div>

                    <div className="field-group">
                      <label className="field-label">
                        <span>Package Name</span>
                        <textarea
                          placeholder="e.g. Signature Portrait Session&#10;or multi-line package name"
                          value={editing.name || ''}
                          onChange={e => {
                            const name = e.target.value;
                            const auto = toSlug(name.split('\n')[0]); // Use first line for slug
                            const currentSlug = editing.slug || '';
                            const wasAuto = !currentSlug || currentSlug === toSlug((editing.name || '').split('\n')[0]);
                            setEditing({ ...editing, name, slug: wasAuto ? auto : currentSlug });
                          }}
                          className="form-textarea"
                          rows={2}
                        />
                        <small className="field-hint">Package name can span multiple lines if needed</small>
                      </label>
                      
                      <label className="field-label">
                        <span>Category</span>
                        <select
                          value={editing.category || 'photography'}
                          onChange={e => setEditing({ ...editing, category: e.target.value || 'photography' })}
                          className="form-select"
                        >
                          {categoryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <small className="field-hint">Links to galleries with the same category</small>
                      </label>
                      
                      <label className="field-label">
                        <span>URL Slug</span>
                        <input
                          type="text"
                          placeholder="signature-portrait-session"
                          value={editing.slug || ''}
                          onChange={e => setEditing({ ...editing, slug: toSlug(e.target.value) })}
                          className="form-input"
                        />
                        <small className="field-hint">Auto-generated from name. Used in URLs.</small>
                      </label>
                    </div>
                    
                    <div className="field-group">
                      <label className="field-label">
                        <span>Short Description</span>
                        <textarea
                          placeholder="Brief teaser for package cards and previews..."
                          value={editing.short || ''}
                          onChange={e => setEditing({ ...editing, short: e.target.value })}
                          className="form-textarea"
                          rows={3}
                        />
                        <small className="field-hint">Keep it concise and compelling</small>
                      </label>
                      
                      <label className="field-label">
                        <span>Full Description</span>
                        <textarea
                          placeholder="Detailed description of what's included in this package..."
                          value={editing.description || ''}
                          onChange={e => setEditing({ ...editing, description: e.target.value })}
                          className="form-textarea"
                          rows={4}
                        />
                      </label>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Package Images Section - Gallery Style */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-images"></i>
                    </div>
                    <div className="section-title">
                      <h3>Package Images</h3>
                      <p>Add and manage images for this package (first image becomes hero image)</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    {/* Upload Actions - Restored Working UploadWidget */}
                    <div className="upload-actions">
                      <UploadWidget
                        onUpload={(images: CloudinaryImage[]) => {
                          console.log('🎁 UploadWidget: Images uploaded:', images);
                          handleImageUpload(images);
                        }}
                        buttonText="Upload hero image"
                        folder={editing.name ? `carmen_lorraine_photos/packages/${editing.name.toLowerCase().replace(/\s+/g, '_')}` : 'carmen_lorraine_photos/packages'}
                      />
                      <button
                        type="button"
                        className="browse-hero-btn"
                        onClick={() => setShowHeroImageBrowser(true)}
                      >
                        <i className="fas fa-folder-open"></i>
                        Pick hero from Cloudinary
                      </button>
                    </div>
                    {showHeroImageBrowser && createPortal(
                      <div className="cloudinary-browser-overlay" onClick={() => setShowHeroImageBrowser(false)}>
                        <div className="cloudinary-browser-modal" onClick={(e) => e.stopPropagation()}>
                          <div className="cloudinary-browser-header">
                            <h3>Pick hero image</h3>
                            <button type="button" className="close-btn" onClick={() => setShowHeroImageBrowser(false)}>
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                          <div className="cloudinary-browser-content">
                            <CloudinaryImageBrowser
                              onImagesSelected={(images) => {
                                if (images && images.length > 0) {
                                  const img = images[0];
                                  const heroImage = {
                                    publicId: img.publicId || (img as any).public_id,
                                    secure_url: img.secure_url || img.url,
                                    url: img.url || img.secure_url,
                                    filename: img.filename,
                                    width: img.width,
                                    height: img.height,
                                    bytes: (img as any).bytes
                                  };
                                  setEditing(prev => prev ? { ...prev, heroImage, image: heroImage.secure_url || heroImage.url || '' } : prev);
                                }
                                setShowHeroImageBrowser(false);
                              }}
                              onClose={() => setShowHeroImageBrowser(false)}
                              maxSelection={1}
                              insideModal={true}
                            />
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )}

                    {/* Single Image Display */}
                    {editing.heroImage ? (
                      <div className="package-image-grid">
                        <div className="package-image-item">
                          <div className="image-thumbnail">
                            <img 
                              src={editing.heroImage.secure_url || ''} 
                              alt="Package image"
                              className="thumbnail-image"
                            />
                            <div className="image-overlay">
                              <div className="image-actions">
                                <button 
                                  type="button" 
                                  className="image-action-btn replace-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('🎁 Replace button clicked for image:', editing.heroImage);
                                    // Open file picker for replacement
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.multiple = false;
                                    input.onchange = async (e) => {
                                      const file = (e.target as HTMLInputElement).files?.[0];
                                      if (file) {
                                        // Upload new image to Cloudinary
                                        const formData = new FormData();
                                        formData.append('file', file);
                                          formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'unsigned_upload');
                                          formData.append('folder', 'carmen_lorraine_photos/packages');
                                          
                                          try {
                                            const response = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, {
                                              method: 'POST',
                                              body: formData
                                            });
                                            const result = await response.json();
                                            
                                            const newImage = {
                                              publicId: result.public_id,
                                              secure_url: result.secure_url,
                                              filename: result.original_filename || file.name,
                                              width: result.width,
                                              height: result.height,
                                              bytes: result.bytes
                                            };
                                            
                                            // Replace the single image
                                            setEditing({ 
                                              ...editing, 
                                              heroImage: newImage,
                                              image: newImage.secure_url || ''
                                            });
                                          } catch (error) {
                                            console.error('Error replacing image:', error);
                                            alert('Failed to replace image. Please try again.');
                                          }
                                        }
                                      };
                                      input.click();
                                    }}
                                    title="Replace image"
                                  >
                                    <i className="fas fa-sync-alt"></i>
                                  </button>
                                  <button 
                                    type="button" 
                                    className="image-action-btn remove-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🎁 Remove button clicked for image');
                                      setEditing({ 
                                        ...editing, 
                                        heroImage: undefined,
                                        image: ''
                                      });
                                    }}
                                    title="Remove image"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="image-info">
                              <p className="image-filename">{editing.heroImage.filename || 'Package Image'}</p>
                              {editing.heroImage.width && editing.heroImage.height && (
                                <p className="image-dimensions">{editing.heroImage.width} × {editing.heroImage.height}</p>
                              )}
                            </div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-images-placeholder">
                        <div className="placeholder-content">
                          <i className="fas fa-images"></i>
                          <h4>No Images Added</h4>
                          <p>Upload images or browse existing ones to get started</p>
                        </div>
                      </div>
                    )}
                    
                    <small className="field-hint">
                      First image will be used as the hero image. Recommended: 1200x630px for hero image.
                    </small>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Pricing & Duration Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-money-bill-wave"></i>
                    </div>
                    <div className="section-title">
                      <h3>Pricing & Duration</h3>
                      <p>Set your rates and session length</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <div className="pricing-grid">
                      <label className="field-label">
                        <span>Base Price</span>
                        <input
                          type="number"
                          placeholder="Enter package price"
                          value={editing.price ?? ''}
                          onChange={e => setEditing({ ...editing, price: e.target.value === '' ? undefined : Number(e.target.value) })}
                          className="form-input"
                        />
                      </label>
                      
                      <label className="field-label">
                        <span>Base Currency</span>
                        <select
                          value={editing.currency || 'R'}
                          onChange={e => setEditing({ ...editing, currency: e.target.value as any })}
                          className="form-select"
                        >
                          <option value="R">R (South African Rand)</option>
                          <option value="USD">USD (US Dollar)</option>
                          <option value="EUR">EUR (Euro)</option>
                          <option value="GBP">GBP (British Pound)</option>
                          <option value="CAD">CAD (Canadian Dollar)</option>
                          <option value="AUD">AUD (Australian Dollar)</option>
                        </select>
                        <small className="field-hint">
                          Base currency for this package. Foreign visitors will see prices automatically converted to their local currency with markup applied.
                        </small>
                      </label>
                      
                      <label className="field-label">
                        <span>Duration (minutes)</span>
                        <input
                          type="number"
                          placeholder="Duration in minutes"
                          value={editing.durationMinutes ?? ''}
                          onChange={e => setEditing({ ...editing, durationMinutes: e.target.value === '' ? undefined : Number(e.target.value) })}
                          className="form-input"
                        />
                      </label>
                      
                      <label className="field-label">
                        <span>Special Price</span>
                        <input
                          type="number"
                          placeholder="Special offer price"
                          value={editing.specialPrice ?? ''}
                          onChange={e => setEditing({ ...editing, specialPrice: e.target.value === '' ? undefined : Number(e.target.value) })}
                          className="form-input special-price"
                        />
                        <small className="field-hint">Leave empty for regular pricing</small>
                      </label>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Group Size Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-users"></i>
                    </div>
                    <div className="section-title">
                      <h3>Group Size</h3>
                      <p>Filtering attribute for the Package Finder wizard</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <div className="field-group">
                      <label className="field-label">
                        <span>Group Size</span>
                        <select
                          value={editing.groupSize || ''}
                          onChange={e => {
                            const groupSize = e.target.value as PackageGroupSize | '';
                            setEditing({ 
                              ...editing, 
                              groupSize: groupSize || undefined
                            });
                          }}
                          className="form-input"
                        >
                          <option value="">Select group size...</option>
                          <option value="solo">Solo / Couple (1-2 people)</option>
                          <option value="small">Small Group (3-6 people)</option>
                          <option value="medium">Medium Group (up to 12 people)</option>
                          <option value="large">Large Group (up to 25 people)</option>
                          <option value="verylarge">Very Large Group (greater than 25 people)</option>
                          <option value="any">Any Size (flexible)</option>
                        </select>
                        <small className="field-hint">Number of people this package is designed for</small>
                      </label>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Add-ons Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-plus-circle"></i>
                    </div>
                    <div className="section-title">
                      <h3>Add-ons</h3>
                      <p>Additional services that can be purchased with this package</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <div className="addons-section">
                      <div className="section-controls">
                        <button 
                          type="button" 
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            const newAddon = { name: '', price: 0 };
                            setEditing({ 
                              ...editing, 
                              addons: [...(editing.addons || []), newAddon] 
                            });
                          }}
                        >
                          <i className="fas fa-plus"></i>
                          Add Service
                        </button>
                      </div>
                      
                      <div className="addons-list">
                        {(editing.addons || []).map((addon, index) => (
                          <div key={index} className="addon-item">
                            <div className="addon-controls">
                              <button 
                                type="button"
                                className="remove-addon-btn"
                                onClick={() => {
                                  const newAddons = [...(editing.addons || [])];
                                  newAddons.splice(index, 1);
                                  setEditing({ ...editing, addons: newAddons });
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                            
                            <div className="addon-fields">
                              <label className="field-label">
                                <span>Service Name</span>
                                <input
                                  type="text"
                                  placeholder="e.g. Extra Hour, Additional Locations"
                                  value={addon.name}
                                  onChange={e => {
                                    const newAddons = [...(editing.addons || [])];
                                    newAddons[index] = { ...addon, name: e.target.value };
                                    setEditing({ ...editing, addons: newAddons });
                                  }}
                                  className="form-input"
                                />
                              </label>
                              
                              <label className="field-label">
                                <span>Price</span>
                                <input
                                  type="number"
                                  placeholder="Add-on price"
                                  value={addon.price || ''}
                                  onChange={e => {
                                    const newAddons = [...(editing.addons || [])];
                                    newAddons[index] = { ...addon, price: e.target.value === '' ? undefined : Number(e.target.value) };
                                    setEditing({ ...editing, addons: newAddons });
                                  }}
                                  className="form-input"
                                />
                                <small className="field-hint">Leave empty for free add-on</small>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {(!editing.addons || editing.addons.length === 0) && (
                        <div className="empty-state-small">
                          <i className="fas fa-info-circle"></i>
                          <p>No add-ons configured. Add optional services for this package.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Special Timing Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-calendar-check"></i>
                    </div>
                    <div className="section-title">
                      <h3>Special Offer Timing</h3>
                      <p>Schedule when special pricing is active</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <div className="timing-grid">
                      <label className="field-label">
                        <span>Special Start Date</span>
                        <input
                          type="datetime-local"
                          value={editing.startAt ? new Date(editing.startAt).toISOString().slice(0, 16) : ''}
                          onChange={e => setEditing({ 
                            ...editing, 
                            startAt: e.target.value ? new Date(e.target.value).toISOString() : undefined 
                          })}
                          className="form-input"
                        />
                        <small className="field-hint">When the special offer begins</small>
                      </label>
                      
                      <label className="field-label">
                        <span>Special End Date</span>
                        <input
                          type="datetime-local"
                          value={editing.endAt ? new Date(editing.endAt).toISOString().slice(0, 16) : ''}
                          onChange={e => setEditing({ 
                            ...editing, 
                            endAt: e.target.value ? new Date(e.target.value).toISOString() : undefined 
                          })}
                          className="form-input"
                        />
                        <small className="field-hint">When the special offer ends</small>
                      </label>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Features Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-sparkles"></i>
                    </div>
                    <div className="section-title">
                      <h3>Package Features</h3>
                      <p>Key highlights and benefits</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <label className="field-label">
                      <span>Feature List</span>
                      <textarea
                        placeholder="Enter features separated by new lines&#10;• Professional editing&#10;• High-resolution images&#10;• Online gallery"
                        value={(editing.features || []).join('\n')}
                        onChange={e => {
                          const features = e.target.value.split('\n').filter(f => f.trim());
                          setEditing({ ...editing, features });
                        }}
                        className="form-textarea"
                        rows={4}
                      />
                      <small className="field-hint">One feature per line. Use bullet points or simple text.</small>
                    </label>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Display Options Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-cog"></i>
                    </div>
                    <div className="section-title">
                      <h3>Display Settings</h3>
                      <p>Badges, visibility, and ordering</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <div className="display-grid">
                      <label className="field-label">
                        <span>Badge</span>
                        <select
                          value={editing.badge || ''}
                          onChange={e => setEditing({ ...editing, badge: e.target.value || undefined })}
                          className="form-select"
                        >
                          <option value="">No Badge</option>
                          <option value="Popular">Popular</option>
                          <option value="New">New</option>
                          <option value="Special">Special</option>
                          <option value="Limited">Limited</option>
                        </select>
                      </label>
                      
                      <label className="field-label">
                        <span>Display Order</span>
                        <input
                          type="number"
                          placeholder="Display order"
                          value={editing.order ?? 999}
                          onChange={e => setEditing({ ...editing, order: Number(e.target.value) })}
                          className="form-input"
                        />
                      </label>
                    </div>
                    
                    <div className="checkbox-section">
                      <h4>Package Options</h4>
                      <div className="checkbox-grid">
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={!!editing.featured}
                            onChange={e => setEditing({ ...editing, featured: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Featured</span>
                            <small>Show on homepage</small>
                          </div>
                        </label>
                        
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={editing.available !== false}
                            onChange={e => {
                              const available = e.target.checked;
                              const wasQuotable = editing.forQuoting === true || (editing.forQuoting !== false && editing.available !== false);
                              setEditing({
                                ...editing,
                                available,
                                forQuoting: available ? editing.forQuoting : (wasQuotable ? true : editing.forQuoting),
                              });
                            }}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Available</span>
                            <small>Show on the website</small>
                          </div>
                        </label>

                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={editing.forQuoting === true || (editing.forQuoting !== false && editing.available !== false)}
                            onChange={e => setEditing({ ...editing, forQuoting: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Quote from this package</span>
                            <small>Use it when writing a quotation. It can stay off the website.</small>
                          </div>
                        </label>
                        
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={!!editing.isSpecial}
                            onChange={e => setEditing({ ...editing, isSpecial: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Special Offer</span>
                            <small>Highlight as limited time</small>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Package Customization Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-palette"></i>
                    </div>
                    <div className="section-title">
                      <h3>Modal Customization</h3>
                      <p>Choose which elements appear in this package's modal</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <div className="customization-section">
                      <h4>Modal Components</h4>
                      <div className="checkbox-grid">
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={editing.customization?.showProcessSteps !== false}
                            onChange={e => {
                              console.log('🎁 Process Steps checkbox changed:', e.target.checked);
                              setEditing({ 
                                ...editing, 
                                customization: { 
                                  ...editing.customization, 
                                  showProcessSteps: e.target.checked 
                                } 
                              });
                            }}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Show Process Steps</span>
                            <small>Display "Process" section</small>
                          </div>
                        </label>
                        
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={editing.customization?.showExpectations !== false}
                            onChange={e => {
                              console.log('🎁 Expectations checkbox changed:', e.target.checked);
                              setEditing({ 
                                ...editing, 
                                customization: { 
                                  ...editing.customization, 
                                  showExpectations: e.target.checked 
                                } 
                              });
                            }}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Show Expectations</span>
                            <small>Display "What to Expect" section</small>
                          </div>
                        </label>
                        
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={editing.customization?.showTestimonials !== false}
                            onChange={e => {
                              console.log('🎁 Testimonials checkbox changed:', e.target.checked);
                              setEditing({ 
                                ...editing, 
                                customization: { 
                                  ...editing.customization, 
                                  showTestimonials: e.target.checked 
                                } 
                              });
                            }}
                          />
                          <span className="checkbox-custom"></span>
                          <div className="checkbox-content">
                            <span className="checkbox-label">Show Testimonials</span>
                            <small>Display "What Clients Say" section</small>
                          </div>
                        </label>
                      </div>
                      
                      <label className="field-label">
                        <span>Custom Booking Note</span>
                        <textarea
                          placeholder="Leave empty to use global booking note..."
                          value={editing.customization?.customBookingNote || ''}
                          onChange={e => setEditing({ 
                            ...editing, 
                            customization: { 
                              ...editing.customization, 
                              customBookingNote: e.target.value 
                            } 
                          })}
                          className="form-textarea"
                          rows={3}
                        />
                        <small className="field-hint">Override the default booking note for this package only</small>
                      </label>

                      {/* Selection of global items for this package */}
                      <div className="selection-grid">
                        <div className="selection-column">
                          <h4>Pick Process Steps</h4>
                          <small>Select none to include all</small>
                          <div className="selection-list">
                            {(getGlobalCustomization().processSteps || []).length === 0 ? (
                              <div className="field-hint">No process steps configured yet. Configure them in Admin → Package text.</div>
                            ) : (getGlobalCustomization().processSteps || []).map((step: any) => {
                              const selected = (editing.customization?.selectedProcessStepIds || []).includes(step.id);
                              return (
                                <label key={step.id} className={`select-item ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = new Set(editing.customization?.selectedProcessStepIds || []);
                                      if (e.target.checked) current.add(step.id); else current.delete(step.id);
                                      setEditing({
                                        ...editing,
                                        customization: {
                                          ...editing.customization,
                                          selectedProcessStepIds: Array.from(current)
                                        }
                                      });
                                    }}
                                  />
                                  <span className="select-title">{step.title} <small>(Original: #{step.number})</small></span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="selection-column">
                          <h4>Pick What to Expect</h4>
                          <small>Select none to include all</small>
                          <div className="selection-list">
                            {(getGlobalCustomization().expectations || []).length === 0 ? (
                              <div className="field-hint">No expectations configured yet. Configure them in Admin → Package text.</div>
                            ) : (getGlobalCustomization().expectations || []).map((item: any) => {
                              const selected = (editing.customization?.selectedExpectationIds || []).includes(item.id);
                              return (
                                <label key={item.id} className={`select-item ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = new Set(editing.customization?.selectedExpectationIds || []);
                                      if (e.target.checked) current.add(item.id); else current.delete(item.id);
                                      setEditing({
                                        ...editing,
                                        customization: {
                                          ...editing.customization,
                                          selectedExpectationIds: Array.from(current)
                                        }
                                      });
                                    }}
                                  />
                                  <span className="select-title"><i className={item.icon} style={{ marginRight: 8 }}></i>{item.title}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="selection-column">
                          <h4>Pick Testimonials</h4>
                          <small>Select none to include all</small>
                          <div className="selection-list">
                            {(getGlobalCustomization().testimonials || []).length === 0 ? (
                              <div className="field-hint">No testimonials configured yet. Configure them in Admin → Package text.</div>
                            ) : (getGlobalCustomization().testimonials || []).map((t: any) => {
                              const selected = (editing.customization?.selectedTestimonialIds || []).includes(t.id);
                              return (
                                <label key={t.id} className={`select-item ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = new Set(editing.customization?.selectedTestimonialIds || []);
                                      if (e.target.checked) current.add(t.id); else current.delete(t.id);
                                      setEditing({
                                        ...editing,
                                        customization: {
                                          ...editing.customization,
                                          selectedTestimonialIds: Array.from(current)
                                        }
                                      });
                                    }}
                                  />
                                  <span className="select-title">{t.author} — {t.service}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Multi-Currency Pricing Section */}
                <div className="form-section-card">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-globe"></i>
                    </div>
                    <div className="section-title">
                      <h3>Multi-Currency Pricing</h3>
                      <p>Automatic currency conversions for international clients</p>
                    </div>
                  </div>
                  
                  <div className="form-fields">
                    <CurrencyConversionTable package={editing} />
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => upsertPackage(editing!)} 
                      disabled={saving} 
                      className="save-button"
                      style={{ minWidth: '120px' }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Package Name Display - At Bottom */}
                {!editing.isNew && editing.name && (
                  <div className="package-name-footer">
                    <div className="package-name-badge">
                      <i className="fas fa-tag"></i>
                      <span className="package-name-label">Package:</span>
                      <span className="package-name-value">{editing.name}</span>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
            
            <div className="service-modal-footer">
              <button onClick={() => setEditing(null)} className="cancel-button">
                <i className="fas fa-times"></i>
                Cancel
              </button>
                   <button onClick={() => upsertPackage(editing!)} disabled={saving} className="save-button">
                     {saving ? (
                       <>
                         <i className="fas fa-spinner fa-spin"></i>
                         Saving...
                       </>
                     ) : (
                       <>
                         <i className="fas fa-save"></i>
                         Save Package
                       </>
                     )}
                   </button>
             </div>
           </div>
        </div>,
        document.body
      )}

      {/* Package Creation Wizard */}
      <PackageCreateWizard
        isOpen={showWizard}
        onClose={() => {
          setShowWizard(false);
          setEditingPackageForWizard(null);
        }}
        editingPackage={editingPackageForWizard}
        onSave={(packageData) => {
          // Convert wizard data to EditState format
          const toSlug = (val: string) => (val || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          
          const packageToSave: EditState = {
            ...(editingPackageForWizard ? { ...editingPackageForWizard } : blankPackage()),
            name: packageData.name || '',
            slug: packageData.slug || toSlug(packageData.name || ''),
            short: packageData.short || '',
            description: packageData.description || '',
            category: packageData.category || 'photography',
            categories: (packageData as any).categories || undefined, // Save multiple categories
            durationMinutes: packageData.durationMinutes,
            groupSize: packageData.groupSize,
            price: packageData.price,
            currency: packageData.currency || 'R',
            specialPrice: packageData.specialPrice,
            isSpecial: packageData.isSpecial,
            startAt: packageData.startAt,
            endAt: packageData.endAt,
            basePriceIncluded: (packageData as any).basePriceIncluded,
            addons: packageData.addons,
            features: packageData.features,
            badge: packageData.badge,
            featured: packageData.featured,
            available: packageData.available !== false,
            order: packageData.order || 999,
            customization: packageData.customization,
            heroImage: packageData.heroImage,
            image: packageData.image || packageData.heroImage?.secure_url,
          };

          // Save the package
          upsertPackage(packageToSave);
          setEditingPackageForWizard(null);
        }}
      />
    </section>
  );
};

export default PackageManagement;


